import { useDispatch, useSelector } from "react-redux";
import {
  commonActions,
  selectAvailableIconAssets,
  selectUnavailableIconLinks,
} from "@shared/slices/common.slice";
import {
  getAssetIcon,
  getExchangeIcon,
  getIconAssetName,
} from "@shared/utils/misc";
import { ICON_TYPES } from "@shared/common/constants";
import { IconType } from "@shared/types";

export interface useIconWithFallbackProps {
  coinOrExchangeName?: string;
  iconType?: IconType;
  iconLink?: string;
  removeOnError?: boolean;
}

export const useIconWithFallback = ({
  coinOrExchangeName,
  iconType,
  iconLink,
  removeOnError = true,
}: useIconWithFallbackProps) => {
  const assetName = getIconAssetName(
    coinOrExchangeName?.split("/")[0].toLowerCase() || ""
  );
  const dispatch = useDispatch();
  const availableIconAssets = useSelector(selectAvailableIconAssets);
  const unavailableIconLinks = useSelector(selectUnavailableIconLinks);

  const checkError = (name?: string, iconLink?: string) => {
    if (name) {
      return (
        iconType === ICON_TYPES.COIN &&
        availableIconAssets.size &&
        !availableIconAssets.has(name)
      );
    } else if (iconLink) {
      return unavailableIconLinks.has(iconLink);
    }
  };
  const error = checkError(assetName, iconLink);

  if (!coinOrExchangeName && !iconLink) return {};

  const getFallbackLetter = () => {
    if (coinOrExchangeName) return coinOrExchangeName?.charAt(0);
    else if (iconLink) {
      return iconLink.split("/").pop()?.charAt(0);
    }
  };

  let iconUrl: string | undefined;
  if (iconLink) {
    iconUrl = iconLink;
  } else if (coinOrExchangeName && iconType === ICON_TYPES.EXCHANGE) {
    iconUrl = getExchangeIcon(coinOrExchangeName);
  } else if (iconType === ICON_TYPES.COIN) {
    iconUrl = getAssetIcon(assetName);
  }

  const handleError = () => {
    if (removeOnError) {
      dispatch(commonActions.removeFromAvailableIconAssets(assetName));
    }
    dispatch(commonActions.addToUnavailableIconLinks(iconLink));
  };

  return { fallbackLetter: getFallbackLetter(), iconUrl, handleError, error };
};
